import { type Dispatch, createContext } from 'react';

import { FormFieldReducerAction } from '../state/formfield-state-reducer.js';
import type { SharedFormFieldProps } from '../types/formfield.types.js';

/**
 * @public
 */
export interface SharedFormFieldContextProps extends SharedFormFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
  dispatch?: Dispatch<FormFieldReducerAction>;
  /** @internal */
  formFieldId?: string;
  /**
   * Logical form-control id registered against this FormField.
   * @internal
   */
  formControlId?: string;
  /**
   * Native/member input ids belonging to the logical form control.
   * @internal
   */
  memberFormControlIds?: string[];
  /**
   * Registers a logical form-control id and returns an unregister callback for
   * React effect cleanup. Custom context consumers may omit this —
   * `useFormFieldContext` falls back to a noop.
   * @internal
   */
  registerFormControlId?: (id: string) => () => void;
  /**
   * Registers a native/member input id and returns an unregister callback for
   * React effect cleanup. Custom context consumers may omit this —
   * `useFormFieldContext` falls back to a noop.
   * @internal
   */
  registerMemberFormControlId?: (id: string) => () => void;
  /** @internal */
  formFieldLabelId?: string;
  /**
   * Registers a label id and returns an unregister callback for React effect
   * cleanup. Custom context consumers may omit this — `useFormFieldContext`
   * falls back to a noop.
   * @internal
   */
  registerFormFieldLabelId?: (id: string) => () => void;
}

const defaultFormFieldContext: SharedFormFieldContextProps = {
  controlId: undefined,
  formFieldId: undefined,
  formControlId: undefined,
  memberFormControlIds: [],
  registerFormControlId: () => () => undefined,
  registerMemberFormControlId: () => () => undefined,
  formFieldLabelId: undefined,
  registerFormFieldLabelId: () => () => undefined,
  required: undefined,
  disabled: undefined,
  ariaDisabled: undefined,
  dispatch: undefined,
  readOnly: undefined,
};

/**
 * @internal
 * Context provider for `FormField` props meant to be provided to all its input-related child nodes.
 */
export const _FormFieldContext = createContext<SharedFormFieldContextProps>(
  defaultFormFieldContext,
);
